import type { ChartAccount } from '@/api/models';

/**
 * Resolving a subsidiary ledger's account reference against the chart.
 *
 * Fixed assets, salaries and dividends are all live, so the account ids they
 * carry are the server's own and match the chart's directly. This used to also
 * fall back to the **account code**, because those registers were mocks whose
 * ids were the seeded fixture's (`a-1210`) and could never match a live chart.
 * That seam is gone with the mocks; the fallback went with it.
 *
 * ## Unresolvable is not zero
 *
 * When the lookup finds no account, this returns `null`, never `0`. The
 * distinction is the whole point: `0` is a ledger balance and would be compared;
 * `null` means "this comparison has no ledger side", and the screen declines to
 * draw a difference it cannot justify rather than reporting one equal to the
 * entire register.
 *
 * That case is real, not theoretical — the three register summaries each carry
 * **nullable** tie-out account ids, and the chart need not carry the payroll,
 * dividend or disposal accounts an entry posted to.
 */

/** The chart row a subsidiary ledger's account reference points at, or `null`. */
export const findLedgerAccount = (
    accounts: readonly ChartAccount[],
    accountId: string,
): ChartAccount | null =>
    accounts.find((account) => account.id === accountId) ?? null;

/**
 * The account's **natural** balance in tetri — the balance in its own normal
 * direction, so a credit-normal payable reports what is owed as a positive
 * magnitude and a contra-asset reports its accumulated amount as a positive
 * magnitude. Using the signed (debit-positive) figure would sign-flip every
 * comparison a register makes.
 *
 * `null` when the account is not in the chart at all. An account that exists and
 * has never been posted to correctly reads `0`.
 */
export const ledgerNaturalTetri = (
    accounts: readonly ChartAccount[],
    accountId: string,
): number | null => {
    const account = findLedgerAccount(accounts, accountId);
    if (!account) {
        return null;
    }
    return account.balance?.naturalBalanceTetri ?? 0;
};

/** The account's code for the "compared against" caption; `—` when unresolved. */
export const ledgerAccountCode = (
    accounts: readonly ChartAccount[],
    accountId: string,
): string => findLedgerAccount(accounts, accountId)?.code ?? '—';

/**
 * Sum the natural balances of several accounts — **all or nothing**.
 *
 * If any one reference fails to resolve, the whole sum is `null`. A partial sum
 * would be compared against the register's *full* total, and the shortfall would
 * be reported as a difference: the register claims cost for accounts the ledger
 * side silently omitted. That is a difference that isn't real, produced by the
 * integration seam — exactly what this module exists to prevent.
 *
 * An empty reference list is a resolved zero: the register maps to nothing and
 * claims nothing, so the two agree.
 */
export const ledgerNaturalTetriSum = (
    accounts: readonly ChartAccount[],
    accountIds: readonly string[],
): number | null => {
    let total = 0;
    for (const accountId of accountIds) {
        const natural = ledgerNaturalTetri(accounts, accountId);
        if (natural === null) {
            return null;
        }
        total += natural;
    }
    return total;
};

/** The codes of the accounts that did resolve, sorted, for the caption. */
export const ledgerAccountCodes = (
    accounts: readonly ChartAccount[],
    accountIds: readonly string[],
): string[] =>
    accountIds
        .map((accountId) => findLedgerAccount(accounts, accountId))
        .filter((account): account is ChartAccount => account !== null)
        .map((account) => account.code)
        .sort();
